import React, { useState, useEffect, Fragment } from 'react'
import { useField, useFormikContext } from 'formik'
import { useTranslation } from 'react-i18next'

import {
  BridgeFormValues,
  MappedField,
  MappedFieldFormula,
  MappedFieldMapping,
} from 'Nbee'

import { Button } from '@components/Basic/ButtonNbe'
import { Loader } from '@components/Basic/Loader'
import { Modal } from '@components/Basic/Modal'
import { InputField } from '@app/components/Form/InputField'
import {
  InputSmartSelect,
  SelectValue,
} from '@components/Form/InputSmartSelect'
import { InputSmartCreatableSelect } from '@app/components/Form/InputSmartSelect/Creatable'

import {
  FormulaModalBody,
  FormulaModalCloseIcon,
  FormulaModalFooter,
  FormulaModalHeader,
  FormulaModalHero,
  FormulaModalTab,
  FormulaModalTabs,
  FormulaModalWrapper,
  FormulaTestResult,
} from '@features/nbee/FieldsMappingForm/FormulaModal/styled'
import { IoArrowBackOutline, IoCloseOutline } from 'react-icons/io5'
import ReactTooltip from 'react-tooltip'

import { useGetSingleFormulaSchema } from '@app/api/getSingleFormulaSchema'
import { useTestFormula } from '@app/api/postTestFormula'

interface ModalProps {
  selectedMapping?: MappedFieldMapping
  isOpen: boolean
  onCloseModal: (formula: MappedFieldFormula) => void
  sourceFieldOptions?: SelectValue[] | []
  index: number
}

type FormulaModalTabId = 'configuration' | 'test'

export const FormulaModal: React.FC<ModalProps> = ({
  selectedMapping,
  isOpen,
  onCloseModal,
  sourceFieldOptions,
  index,
}) => {
  const mappingId = selectedMapping?.id
  const formulaId = selectedMapping?.formula?.id
  const isNew = selectedMapping?.formula?.isNew
  const modalTitle = isNew ? 'Add' : 'Edit'

  const { t } = useTranslation()

  // Extract information from Formik context
  const { values } = useFormikContext<BridgeFormValues>()

  const fieldName = `fieldsMapping.${index}`
  const [field, meta, helpers] = useField<MappedField>(fieldName)

  const currentFieldValue = field.value
  const currentFieldMappingWithFormula = field.value.mapping.find(
    (m) => m.formula && m.id === mappingId // Matches the mapping ID
  )

  // Now you can access the formula and its params directly
  const currentFieldFormula = currentFieldMappingWithFormula?.formula
  const currentFieldFormulaParams = currentFieldFormula?.params

  // Create a temporary state to not save immediately in Formik context
  const [formulaTemporaryState, setFormulaTemporaryState] =
    useState<MappedFieldFormula>(currentFieldFormula as MappedFieldFormula)

  // Disable submit button if there are errors in the form
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] =
    useState<boolean>(false)

  const [activeTab, setActiveTab] = useState<FormulaModalTabId>('configuration')

  // Values used in the test tab: they live only while the modal is open and are
  // never propagated to the formula configured in the configuration tab
  const [testFormulaState, setTestFormulaState] =
    useState<MappedFieldFormula | null>(null)

  // Retrieve single formula schema, passing bridge ID and formula ID
  const { data: singleFormulaSchema, isLoading: isGettingSingleFormulaSchema } =
    useGetSingleFormulaSchema(`${values.ui?.bridgeId}`, formulaId)

  const {
    mutate: testFormula,
    data: testFormulaResponse,
    error: testFormulaError,
    isLoading: isTestingFormula,
    reset: resetTestFormula,
  } = useTestFormula(`${values.ui?.bridgeId}`)

  // Memoize formula name, formula description and param title to avoid re-rendering
  const formulaName = React.useMemo(
    () =>
      singleFormulaSchema && singleFormulaSchema.data.length
        ? `${singleFormulaSchema?.data[0]?.category} >> ${t(
            `nbee.formulas.${singleFormulaSchema?.data[0]?.id}.Name` as any
          )}`
        : undefined,
    [t, singleFormulaSchema]
  )
  const formulaDescription = React.useMemo(
    () =>
      singleFormulaSchema
        ? t(
            `nbee.formulas.${singleFormulaSchema?.data[0]?.id}.Description` as any
          )
        : undefined,
    [t, singleFormulaSchema]
  )
  const getParamDescription = React.useMemo(
    () => (paramId: string) =>
      t(
        `nbee.formulas.${singleFormulaSchema?.data[0]?.id}.Params.${paramId}` as any
      ),
    [t, singleFormulaSchema]
  )

  const singleFormulaSchemaParams = singleFormulaSchema?.data[0]?.params || []

  // if there are no params in the formik context it's because we've just changed the formula.
  // in that case, we need to load in the state the params of the new formula
  useEffect(() => {
    if (singleFormulaSchema && !currentFieldFormulaParams?.length) {
      setFormulaTemporaryState({
        ...(currentFieldFormula as MappedFieldFormula),
        params:
          singleFormulaSchema?.data[0]?.params?.map((p, index) => {
            return {
              ...p,
              required: p.type === 'field' && index === 0,
            }
          }) || [],
      })
    } else if (singleFormulaSchema && currentFieldFormulaParams?.length) {
      // otherwise, we need to update the state with the params of the formik context
      setFormulaTemporaryState({
        ...(currentFieldFormula as MappedFieldFormula),
        params:
          currentFieldFormulaParams?.map((p) => ({
            ...p,
            values: p.values,
            required: p.type === 'field' && index === 0,
          })) || [],
      })
    }
  }, [singleFormulaSchema])

  // Test values are kept while the modal stays open and are wiped on close
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('configuration')
      setTestFormulaState(null)
      resetTestFormula()
    }
  }, [isOpen])

  const handleValueChange = (
    paramId: string,
    value: string | string[] | number
  ) => {
    if (!formulaTemporaryState) {
      return undefined
    }

    const updatedFormula: MappedFieldFormula = {
      id: formulaTemporaryState.id,
      params: [...formulaTemporaryState?.params] || [],
    }
    const paramIndex = updatedFormula.params.findIndex((p) => p.id === paramId)
    if (paramIndex >= 0) {
      updatedFormula.params[paramIndex].values = value as string | string[]
    }

    // Handle field value change and update state, not formik context
    setFormulaTemporaryState(updatedFormula)
  }

  const handleTestValueChange = (
    paramId: string,
    value: string | string[] | number
  ) => {
    setTestFormulaState((previousState) => {
      if (!previousState) {
        return previousState
      }
      return {
        ...previousState,
        params: previousState.params.map((p) =>
          p.id === paramId ? { ...p, values: value as string | string[] } : p
        ),
      }
    })
  }

  const getOptionsForSelectDropdown = (paramId: string) => {
    const selectedParam = singleFormulaSchema?.data[0]?.params?.find(
      (p) => p.id === paramId
    )
    if (Array.isArray(selectedParam?.values) && selectedParam?.values) {
      return selectedParam?.values.map(
        (v) => ({ value: v, label: v } as SelectValue)
      )
    } else {
      return []
    }
  }

  const getDefaultValueForSelect = (
    paramId: string,
    formulaState: MappedFieldFormula | null,
    onValueChange: (paramId: string, value: string | string[] | number) => void
  ) => {
    if (formulaState?.isNew) return undefined
    if (!formulaState?.params || !sourceFieldOptions) return undefined

    const selectedParam = formulaState?.params.find((p) => p.id === paramId)
    if (!selectedParam) {
      return undefined
    }
    if (selectedParam.type === 'field') {
      return sourceFieldOptions.find(
        (option) => option.value === selectedParam.values
      )
    }
    if (selectedParam.type === 'dropdown') {
      const dropdownOptions = getOptionsForSelectDropdown(paramId)
      let defaultValue = dropdownOptions?.find(
        (option) => option.value === selectedParam.values
      )
      if (defaultValue === undefined && dropdownOptions.length > 0) {
        defaultValue = {
          value: dropdownOptions[0].value,
          label: dropdownOptions[0].value,
        } as SelectValue
        onValueChange(paramId, dropdownOptions[0].value)
      }

      return defaultValue
    }
  }

  const getDefaultValueForCreatableSelect = (
    paramId: string,
    formulaState: MappedFieldFormula | null
  ) => {
    if (!formulaState?.params?.length) {
      return undefined
    }
    const selectedParam = formulaState?.params.find((p) => p.id === paramId)
    return (selectedParam?.values as string[]) || []
  }

  const transformFieldId = (paramId?: string): string | null => {
    if (!paramId) return null

    return paramId
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/field\.(\d+)/gi, (_, num) => `Field ${Number(num) + 1}`)
      .replace(/\b(\w)/g, (char) => char.toUpperCase())
  }

  // Handle submit button disabled state
  useEffect(() => {
    if (isGettingSingleFormulaSchema) {
      setIsSubmitButtonDisabled(true)
    }
    if (!formulaTemporaryState?.params[0]?.values) {
      setIsSubmitButtonDisabled(true)
    } else {
      setIsSubmitButtonDisabled(false)
    }
  }, [formulaTemporaryState])

  const handleTabChange = (tab: FormulaModalTabId) => {
    // Entering the test tab for the first time we clone the configured formula,
    // emptying every `field` param: there the user types a literal value to test
    if (tab === 'test' && !testFormulaState && formulaTemporaryState) {
      setTestFormulaState({
        ...formulaTemporaryState,
        isNew: false,
        params: formulaTemporaryState.params.map((p) =>
          p.type === 'field' ? { ...p, values: '' } : { ...p }
        ),
      })
    }
    setActiveTab(tab)
  }

  const handleTestFormula = () => {
    if (!testFormulaState) {
      return undefined
    }

    testFormula({
      fieldType: 'formula',
      sourceFieldId: null,
      text: null,
      formula: {
        id: testFormulaState.id,
        // strip UI only properties (`required`, `isNew`) that the api doesn't expect
        params: testFormulaState.params.map(({ id, order, type, values }) => ({
          id,
          order,
          type,
          values,
        })),
      },
    })
  }

  // Copies every value typed in the test tab back into the configuration one,
  // except the `field` params: there the user types the literal value to
  // evaluate, which has no meaning as a source field of the configuration
  const handleApplyTestValuesToConfiguration = () => {
    if (!testFormulaState || !formulaTemporaryState) {
      return undefined
    }

    setFormulaTemporaryState({
      ...formulaTemporaryState,
      // the configuration is not untouched anymore, so the selects have to
      // render the applied values instead of their empty placeholder
      isNew: false,
      params: formulaTemporaryState.params.map((param) => {
        if (param.type === 'field') {
          return param
        }
        const testParam = testFormulaState.params.find((p) => p.id === param.id)
        return testParam ? { ...param, values: testParam.values } : param
      }),
    })

    handleTabChange('configuration')
  }

  const handleAbort = () => {
    // Find the index of the mapping formula to reset
    const mappingIndex = currentFieldValue.mapping.findIndex(
      (m) => m.id === mappingId
    )

    if (mappingIndex !== -1 && isNew) {
      // Remove the mapping formula from the array only when it's a new formula
      const newMappings = currentFieldValue.mapping.filter(
        (m, i) => i !== mappingIndex
      )

      // Update the MappedField with the modified mappings
      helpers.setValue({
        ...currentFieldValue,
        mapping: newMappings,
      })
    }
    // if we're editing an existing formula, don't do anything

    onCloseModal(currentFieldFormula as MappedFieldFormula)
  }

  const handleSubmit = () => {
    // Create a new mapping array by mapping over the currentFieldValue.mapping
    const updatedMapping = currentFieldValue.mapping.map((mapping) => {
      // Check if the current mapping is the one being edited (matched by mappingId)
      if (mapping.id === mappingId) {
        // If it is, return a new mapping object with the updated formulaTemporaryState
        return {
          ...mapping,
          formula: { ...formulaTemporaryState, isNew: false },
        }
      }
      // For all other mappings, return them unchanged
      return mapping
    })

    // Set the updated mapping array back into the Formik state
    helpers.setValue({
      ...currentFieldValue,
      mapping: updatedMapping,
    })

    // Close the modal and pass the updated formula back as a confirmation (if needed)
    onCloseModal(formulaTemporaryState)
  }

  const isTestTab = activeTab === 'test'
  const currentTabFormulaState = isTestTab
    ? testFormulaState
    : formulaTemporaryState
  const handleCurrentTabValueChange = isTestTab
    ? handleTestValueChange
    : handleValueChange

  const isTestButtonDisabled =
    isTestingFormula || !testFormulaState?.params[0]?.values

  // nothing to apply when the formula only asks for the value to evaluate
  const canApplyToConfiguration = singleFormulaSchemaParams.some(
    (param) => param.type !== 'field'
  )

  const testFormulaErrorMessage = testFormulaError
    ? typeof testFormulaError.response?.data?.error?.message === 'string'
      ? testFormulaError.response?.data?.error?.message
      : 'Something went wrong while testing this formula, please try again.'
    : undefined

  // in the test tab a `field` param is not a source field anymore: the user types
  // the literal value to evaluate, so the label changes accordingly
  const getParamLabel = (paramId: string, paramType: string): string | null => {
    const label = transformFieldId(paramId)
    if (!isTestTab || paramType !== 'field') {
      return label
    }
    // keeps the trailing index of multi field formulas (`Field 2` > `Input value to evaluate 2`)
    return label
      ? label.replace(/^Field(\s|$)/, 'Input value$1')
      : 'Input value'
  }

  const renderParamInput = (paramId: string, paramType: string) => {
    if (!currentTabFormulaState) {
      return null
    }

    // In the test tab a `field` param is a free text input where the user types
    // the value to test, instead of the dropdown with the source fields
    if (paramType === 'field') {
      return isTestTab ? (
        <InputField
          name={`test-${paramId}`}
          type={'text'}
          value={
            (currentTabFormulaState.params.find((p) => p.id === paramId)
              ?.values as string) || ''
          }
          onChange={(e) => handleCurrentTabValueChange(paramId, e.target.value)}
        />
      ) : (
        <InputSmartSelect
          initialValues={sourceFieldOptions || []}
          defaultValue={getDefaultValueForSelect(
            paramId,
            currentTabFormulaState,
            handleCurrentTabValueChange
          )}
          onSelect={(selectedValue) => {
            const singleValue = selectedValue as SelectValue
            if (singleValue) {
              handleCurrentTabValueChange(paramId, singleValue.value)
            }
          }}
          upDownIconsStyle
          isClearable
        />
      )
    }

    if (paramType === 'dropdown') {
      return (
        <InputSmartSelect
          initialValues={getOptionsForSelectDropdown(paramId)}
          defaultValue={getDefaultValueForSelect(
            paramId,
            currentTabFormulaState,
            handleCurrentTabValueChange
          )}
          onSelect={(selectedValue) => {
            const singleValue = selectedValue as SelectValue
            if (singleValue) {
              handleCurrentTabValueChange(paramId, singleValue.value)
            }
          }}
          upDownIconsStyle
          isClearable
        />
      )
    }

    if (paramType === 'text' || paramType === 'char' || paramType === 'int') {
      return (
        <InputField
          name={`${isTestTab ? 'test-' : ''}${paramId}`}
          type={'text'}
          maxLength={paramType === 'char' ? 1 : undefined}
          value={
            (currentTabFormulaState.params.find((p) => p.id === paramId)
              ?.values as string) || ''
          }
          onChange={(e) => handleCurrentTabValueChange(paramId, e.target.value)}
        />
      )
    }

    if (paramType === 'tags') {
      return (
        <InputSmartCreatableSelect
          name={`${isTestTab ? 'test-' : ''}${paramId}`}
          placeholder={''}
          defaultValue={getDefaultValueForCreatableSelect(
            paramId,
            currentTabFormulaState
          )}
          onSelect={(newValues) =>
            handleCurrentTabValueChange(paramId, newValues)
          }
          isClearable={false}
        />
      )
    }

    return null
  }

  return (
    <Modal
      noPadding
      isOpen={isOpen}
      onCloseModal={() =>
        onCloseModal(currentFieldFormula as MappedFieldFormula)
      }
    >
      {isGettingSingleFormulaSchema && (
        <Loader $active $dimmer $text='Populating fields...' />
      )}

      <FormulaModalWrapper>
        <FormulaModalHeader>
          <div>
            <h3>
              {modalTitle} {(formulaName && formulaName.toUpperCase()) || ''}
            </h3>
            <FormulaModalHero>
              <small>{formulaDescription}</small>
            </FormulaModalHero>
          </div>
          <FormulaModalCloseIcon onClick={handleAbort}>
            <IoCloseOutline />
          </FormulaModalCloseIcon>
        </FormulaModalHeader>

        <FormulaModalTabs>
          <FormulaModalTab
            type={'button'}
            $isActive={!isTestTab}
            onClick={() => handleTabChange('configuration')}
          >
            {'Configuration'}
          </FormulaModalTab>
          <FormulaModalTab
            type={'button'}
            $isActive={isTestTab}
            onClick={() => handleTabChange('test')}
          >
            {'Test'}
          </FormulaModalTab>
        </FormulaModalTabs>

        <FormulaModalBody>
          {singleFormulaSchemaParams &&
            singleFormulaSchemaParams.map((param) => (
              <Fragment
                key={`${param.id}-${activeTab}-${isOpen ? 'open' : 'closed'}`}
              >
                <div style={{ marginTop: '.6rem' }}>
                  <h5>{getParamLabel(param.id, param.type)}</h5>
                  <small
                    style={{
                      display: 'inline-block',
                      maxWidth: '40rem',
                      wordWrap: 'break-word',
                    }}
                  >
                    {getParamDescription(param.id)}
                  </small>
                </div>
                <div style={{ margin: '.6rem 0' }}>
                  {renderParamInput(param.id, param.type)}
                </div>
              </Fragment>
            ))}
          {isTestTab &&
            (isTestingFormula ||
              testFormulaResponse ||
              testFormulaErrorMessage) && (
              <FormulaTestResult $isError={!!testFormulaErrorMessage}>
                <h5>{'Result'}</h5>
                {isTestingFormula
                  ? 'Testing formula...'
                  : testFormulaErrorMessage ||
                    testFormulaResponse?.data?.result}
              </FormulaTestResult>
            )}
        </FormulaModalBody>

        <FormulaModalFooter>
          {isTestTab ? (
            <>
              {canApplyToConfiguration && (
                <>
                  <ReactTooltip
                    id={'formula-apply-to-configuration'}
                    place={'top'}
                    type={'dark'}
                    textColor={'white'}
                    effect={'solid'}
                  >
                    <span>
                      {
                        'Click this button to copy all the fields to tab "Configuration"'
                      }
                    </span>
                  </ReactTooltip>
                  <Button
                    type={'button'}
                    $variant={'primary'}
                    onClick={handleApplyTestValuesToConfiguration}
                    $hasIcon
                    data-tip
                    data-for={'formula-apply-to-configuration'}
                  >
                    <IoArrowBackOutline />
                    {'Apply to configuration'}
                  </Button>
                </>
              )}
              <Button
                type={'button'}
                $variant={'primary'}
                onClick={handleTestFormula}
                disabled={isTestButtonDisabled}
              >
                {'Test'}
              </Button>
            </>
          ) : (
            <>
              <Button
                type={'button'}
                $variant={'secondary'}
                onClick={handleAbort}
              >
                {'Abort'}
              </Button>
              <Button
                type={'button'}
                $variant={'primary'}
                onClick={handleSubmit}
                disabled={isSubmitButtonDisabled}
              >
                {'Confirm'}
              </Button>
            </>
          )}
        </FormulaModalFooter>
      </FormulaModalWrapper>
    </Modal>
  )
}
